import React, { useEffect, useRef, useState } from "react";
import { Button } from "react-bootstrap";
import { postRoomOccupant, patchRoomOccupant } from "../../services/httpClient";
import {
  HousingCandidate,
  CandidateRow,
  buildCaseCandidateRows,
  getRoomMode,
  RoomMode,
  pluralizePeople,
  roomDisplayName,
  MAX_FAMILY_ROOM_BEDS,
} from "../../helpers/housingAssignment";
import CandidateSearchPanel from "./CandidateSearchPanel";
import InlineConfirm from "../ui/InlineConfirm";

interface RoomAssignmentActionsArgs {
  room: any;
  facilities: any[];
  rooms: any[];
  beds: any[];
  housingCandidates: HousingCandidate[];
  housingByCaseId: Map<number, HousingCandidate>;
  onAssigned: any;
  onPickerOpenChange: any;
  setErrMessage: any;
  setShowToast: any;
  setToastText: any;
  setToastBody: any;
}

interface RoomAssignmentActionsResult {
  mode: RoomMode;
  capacity: number;
  statusDotClass: string;
  statusLabel: string;
  capacityNote: string;
  confirmingEnd: boolean;
  canAssignFamily: boolean;
  pickerOpen: boolean;
  actions: React.ReactNode;
  pickerPanel: React.ReactNode;
}

// Status chip + Assign Family / disabled note / End Assignment behavior for one room -
// shared between the normal room row and the dedicated many-beds drill-in view, since
// both need identical family-assignment behavior. Returns pieces so each caller can
// arrange them in its own layout.
export default function useRoomAssignmentActions({
  room,
  facilities,
  rooms,
  beds,
  housingCandidates,
  housingByCaseId,
  onAssigned,
  onPickerOpenChange,
  setErrMessage,
  setShowToast,
  setToastText,
  setToastBody,
}: RoomAssignmentActionsArgs): RoomAssignmentActionsResult {
  const [familyPickerOpen, setFamilyPickerOpen] = useState(false);
  const [familySearch, setFamilySearch] = useState("");
  const [confirmEnd, setConfirmEnd] = useState(false);

  const roomBeds = beds.filter((b: any) => b.room_id === room.id);
  const mode = getRoomMode(room, beds);
  const filled = roomBeds.filter((b: any) => b.occupant_id).length;
  const capacity = roomBeds.length;
  const familyCase = mode === "family" ? housingByCaseId.get(room.family_case_id) : undefined;

  let statusDotClass = "role-dot role-dot-green";
  let statusLabel = "Vacant";
  if (mode === "family" && familyCase) {
    statusDotClass = "role-dot role-dot-orange";
    statusLabel = `Occupied · ${familyCase.name} · ${pluralizePeople(familyCase.size)}`;
  } else if (mode === "individual") {
    statusDotClass = "role-dot role-dot-blue";
    statusLabel = `Individual · ${filled} of ${capacity} beds filled`;
  }

  // A dropped connection or a gateway error carries no response body, and an empty message
  // renders off-screen - the assignment would appear to have done nothing at all.
  const onError = (err: any) => {
    setErrMessage(err?.response?.data?.msg || "Could not save that housing change");
    window.scrollTo({ top: 0, behavior: "auto" });
  };

  // The parent counts open pickers to lock its filters, so an unmount while one is open -
  // collapsing the facility, say - would leave that count stranded above zero forever.
  const pickerOpenRef = useRef(false);
  useEffect(() => {
    pickerOpenRef.current = familyPickerOpen;
  }, [familyPickerOpen]);
  useEffect(
    () => () => {
      if (pickerOpenRef.current) onPickerOpenChange(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const onClickAssignFamily = () => {
    setErrMessage("");
    setFamilySearch("");
    // not inside the updater: it must stay pure, and React may run it more than once
    const next = !familyPickerOpen;
    setFamilyPickerOpen(next);
    onPickerOpenChange(next);
  };

  const onPickFamilyCase = (row: CandidateRow) => {
    const candidate: HousingCandidate = row.candidate;
    setErrMessage("");
    postRoomOccupant({ room_id: room.id }, candidate.case_id as any)
      .then(() => {
        setShowToast(true);
        setToastText("Room Assigned");
        setToastBody(`${candidate.name} — ${roomDisplayName(room, facilities)}`);
        setFamilyPickerOpen(false);
        onPickerOpenChange(false);
        onAssigned();
      })
      .catch(onError);
  };

  const onConfirmEndFamily = () => {
    setErrMessage("");
    patchRoomOccupant(room.family_case_id, room.bed_room_occupant_id)
      .then(() => {
        setShowToast(true);
        setToastText("Room Assignment Ended");
        setToastBody(roomDisplayName(room, facilities));
        setConfirmEnd(false);
        onAssigned();
      })
      .catch(onError);
  };

  const canAssignFamily = mode === "vacant" && capacity <= MAX_FAMILY_ROOM_BEDS;

  const actions = (
    <>
      {canAssignFamily && (
        <Button
          size="sm"
          className={familyPickerOpen ? "app-primary-color button" : "app-secondary-color button"}
          onClick={onClickAssignFamily}
        >
          {familyPickerOpen ? "Cancel" : "Assign Case"}
        </Button>
      )}
      {mode === "family" && !confirmEnd && (
        <Button size="sm" className="app-danger-color button" onClick={() => setConfirmEnd(true)}>
          Vacate Case
        </Button>
      )}
      {mode === "family" && confirmEnd && (
        <InlineConfirm
          question={`End ${familyCase?.name}'s stay here?`}
          onConfirm={onConfirmEndFamily}
          onCancel={() => setConfirmEnd(false)}
        />
      )}
    </>
  );

  const pickerPanel = familyPickerOpen ? (
    <CandidateSearchPanel
      placeholder="Search cases by name"
      search={familySearch}
      onSearchChange={setFamilySearch}
      rows={buildCaseCandidateRows(housingCandidates, {
        targetKind: "room",
        targetHousingId: room.id,
        capacity,
        search: familySearch,
        rooms,
        facilities,
        beds,
      })}
      onPick={onPickFamilyCase}
      emptyText="No cases match this search."
    />
  ) : null;

  const capacityNote = mode === "vacant" && !canAssignFamily ? "Too many beds for case assignment" : "";

  return {
    mode,
    capacity,
    statusDotClass,
    statusLabel,
    capacityNote,
    confirmingEnd: confirmEnd,
    canAssignFamily,
    pickerOpen: familyPickerOpen,
    actions,
    pickerPanel,
  };
}
